import { Injectable } from '@angular/core';
import { DestroyableComponent } from '@models/destroyable.component';
import { BehaviorSubject, Subject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { IStep } from './stepper.models';

export interface StepperState {
  currentStepId: number;
  totalSteps: number;
  canNavigate: boolean;
  hasErrors: boolean;
}

@Injectable()
export class StepperService extends DestroyableComponent {
  private check = this.register(new Subject<void>());
  public check$ = this.check.asObservable();

  private isOk = this.register(new Subject<boolean>());
  public isOk$ = this.isOk.asObservable();

  private stepperState = this.register(
    new BehaviorSubject<StepperState>({
      currentStepId: 0,
      totalSteps: 0,
      canNavigate: true,
      hasErrors: false,
    }),
  );

  public stepperState$ = this.stepperState.asObservable().pipe(distinctUntilChanged());

  constructor() {
    super();
  }

  public checkCurrentStep(): void {
    this.check.next();
  }

  public goNextIsOk(isOk: boolean): void {
    this.isOk.next(isOk);
  }

  public updateStepperState(state: Partial<StepperState>): void {
    const currentState = this.stepperState.value;
    this.stepperState.next({ ...currentState, ...state });
  }

  public setCurrentStep(stepId: number): void {
    this.updateStepperState({ currentStepId: stepId });
  }

  public setTotalSteps(count: number): void {
    this.updateStepperState({ totalSteps: count });
  }

  public setCanNavigate(canNavigate: boolean): void {
    this.updateStepperState({ canNavigate });
  }

  public setHasErrors(hasErrors: boolean): void {
    this.updateStepperState({ hasErrors });
  }

  public getCurrentState(): StepperState {
    return this.stepperState.value;
  }

  public validateStep(step: IStep): boolean {
    return !step.disabled && !step.invalid;
  }

  public canNavigateToStep(step: IStep, currentStepId: number): boolean {
    return !step.disabled && step.id !== currentStepId;
  }
}
